import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import * as categoriesApi from "@api/categories.api";
import type { Category } from "@/types";

interface CategoriesState {
  items: Category[];
  loading: boolean;
}

const initialState: CategoriesState = {
  items: [],
  loading: false,
};

export const fetchCategories = createAsyncThunk(
  "categories/fetchAll",
  async () => {
    return await categoriesApi.getAll();
  },
);

export const createCategory = createAsyncThunk(
  "categories/create",
  async (data: { name: string }) => {
    return await categoriesApi.create(data);
  },
);

export const updateCategory = createAsyncThunk(
  "categories/update",
  async ({ id, data }: { id: string; data: { name?: string } }) => {
    return await categoriesApi.update(id, data);
  },
);

export const deleteCategory = createAsyncThunk(
  "categories/delete",
  async (id: string) => {
    await categoriesApi.remove(id);
    return id;
  },
);

const categoriesSlice = createSlice({
  name: "categories",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchCategories.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchCategories.fulfilled, (state, action) => {
        state.items = action.payload;
        state.loading = false;
      })
      .addCase(fetchCategories.rejected, (state) => {
        state.loading = false;
      })
      .addCase(createCategory.fulfilled, (state, action) => {
        state.items.push(action.payload);
        state.items.sort((a, b) => a.name.localeCompare(b.name));
      })
      .addCase(updateCategory.fulfilled, (state, action) => {
        const idx = state.items.findIndex((c) => c.id === action.payload.id);
        if (idx !== -1) state.items[idx] = action.payload;
      })
      .addCase(deleteCategory.fulfilled, (state, action) => {
        state.items = state.items.filter((c) => c.id !== action.payload);
      });
  },
});

export default categoriesSlice.reducer;
